import { Link } from "@tanstack/react-router";
import { SafeImage } from "@/components/site/SafeImage";
import { getRelatedArticles } from "@/lib/article-index";
import { COUNTRIES, roundFlagUrl, type CountrySlug } from "@/data/countries";

interface Props {
  country: CountrySlug;
  slug: string;
  category: string;
}

/**
 * RelatedArticles — appears at the bottom of every article. Uses the
 * category-based selector in `article-index` so newly added articles
 * (same category / same country) are picked up automatically.
 */
export function RelatedArticles({ country, slug, category }: Props) {
  const currentHref = `/${country}/${slug}`;
  const items = getRelatedArticles(currentHref, category, country, 3);
  if (items.length === 0) return null;

  return (
    <section
      aria-labelledby="related-articles-heading"
      className="mt-12 rounded-2xl border border-border bg-card p-6 sm:p-8"
    >
      <div className="flex items-baseline justify-between gap-4">
        <h3
          id="related-articles-heading"
          className="font-display text-2xl font-bold text-foreground"
        >
          Related reads
        </h3>
        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
          {category}
        </span>
      </div>
      <ul className="mt-6 grid gap-4 sm:grid-cols-3">
        {items.map((a) => {
          const c = COUNTRIES.find((x) => x.slug === a.country);
          return (
            <li key={a.href}>
              <Link title={`Read: ${a.title}`}
                to={a.href}
                className="group flex h-full flex-col gap-3 rounded-xl border border-border bg-secondary/40 p-4 transition-all hover:-translate-y-0.5 hover:border-accent/60 hover:bg-card hover:shadow-md"
              >
                <div className="flex items-center gap-2">
                  {c && (
                    <SafeImage
                      src={roundFlagUrl(c.code)}
                      alt={`${c.name} flag`}
                      width={20}
                      height={20}
                      loading="lazy" decoding="async"
                      className="h-5 w-5 rounded-full ring-1 ring-border"
                    />
                  )}
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {a.countryLabel} · {a.category}
                  </span>
                </div>
                <p className="font-display text-base font-semibold leading-snug text-foreground group-hover:text-accent">
                  {a.title}
                </p>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
